/**
 * conversationEntityTracker.js
 *
 * In-memory entity memory per conversation.
 * Enables pronoun resolution: when a user says "su última visita"
 * after asking about Juan, Kevia resolves "su" to Juan's entity context.
 *
 * Cleared when a conversation is deleted or after 30 min of inactivity.
 */

const _state = new Map(); // conversationId → { id, type, name, updatedAt }
const TTL_MS = 30 * 60 * 1000; // 30 min

// Spanish pronouns and demonstratives that signal a reference to a prior entity
const PRONOUN_RE = /\b(su\b|él\b|ella\b|este\b|esta\b|ese\b|esa\b|ellos\b|ellas\b|sus\b)\b/i;

/**
 * Track the entity context for a conversation.
 * Call whenever an explicit entityContext is present in a message.
 *
 * @param {string} conversationId
 * @param {{ id: string, type: string, name?: string }} entityContext
 */
export function trackConversationEntity(conversationId, entityContext) {
  if (!conversationId || !entityContext?.id) return;
  _state.set(conversationId, {
    id:        entityContext.id,
    type:      entityContext.type ?? "unknown",
    name:      entityContext.name ?? "",
    updatedAt: Date.now(),
  });
}

/**
 * Resolve the active entity for a message if it contains pronouns.
 * Returns the last known entity, or null if none / expired.
 *
 * @param {string} conversationId
 * @param {string} messageText
 * @returns {{ id: string, type: string, name: string } | null}
 */
export function resolveConversationEntity(conversationId, messageText) {
  if (!conversationId || !messageText) return null;
  if (!PRONOUN_RE.test(messageText)) return null;

  const cached = _state.get(conversationId);
  if (!cached) return null;

  if (Date.now() - cached.updatedAt > TTL_MS) {
    _state.delete(conversationId);
    return null;
  }

  return cached;
}

/**
 * Get the current tracked entity without pronoun-checking.
 * Returns null if none / expired.
 *
 * @param {string} conversationId
 */
export function getTrackedEntity(conversationId) {
  if (!conversationId) return null;
  const cached = _state.get(conversationId);
  if (!cached || Date.now() - cached.updatedAt > TTL_MS) return null;
  return cached;
}

/**
 * Clear entity state for a deleted or reset conversation.
 *
 * @param {string} conversationId
 */
export function clearConversationEntity(conversationId) {
  _state.delete(conversationId);
}
